import { CheckIcon } from '@heroicons/react/solid';
import { Menu } from '@mantine/core';
import { IconDoorExit } from '@tabler/icons-react';
import { format, parseISO } from 'date-fns';
import { ISchedule, ISlot } from '@gettactic/api';
import { classNames } from '@/lib/classNames';
import { findCheckinTargetSlot } from '@/lib/utils-reserve';
import { TacticTooltip, Tooltip } from '@/components/basic';
import { ReserveCheckTime } from '@/components/reserve/ReserveCheckin/ReserveCheckTime';

type Props = {
  schedule: ISchedule;
  targetSlot: ISlot;
  onCheckin: () => void;
  onCheckout: () => void;
  loading: boolean;
};

function getTitle(schedule: ISchedule, slot: ISlot, hasCheckedIn: boolean) {
  const start = slot.earliest_start_check_in_time;
  const end = slot.end;
  if (schedule.is_pending) {
    return `Your reservation is still pending. It needs to be approved by an admin.`;
  }
  if (!hasCheckedIn) {
    return `Check in time allowed between ${format(parseISO(start.replace('Z', '')), 'h:mm a')} - ${format(
      parseISO(end.replace('Z', '')),
      'h:mm a'
    )} (${schedule.time_zone})`;
  }
  return `You have already checked in`;
}

export default function ReserveCheckInButtonAdmin({
  schedule,
  onCheckout,
  onCheckin,
  targetSlot,
  loading
}: Props) {
  const canCheckIn = !!targetSlot?.capabilities?.can_respond_presence_check_in;
  const canCheckOut = !!targetSlot?.capabilities?.can_respond_presence_check_out;
  const hasCheckedIn = !!targetSlot?.check_in_time;
  const hasCheckedOut = !!targetSlot?.check_out_time;
  const title = getTitle(schedule, targetSlot, hasCheckedIn);
  const disabled =
    schedule.is_pending || !canCheckIn || hasCheckedIn || loading;

  if (hasCheckedOut) {
    return (
      <div
        className="inline-flex items-center rounded-full \
      border border-disabled bg-white px-3 py-1 text-sm \
      text-gray-400 shadow-sm"
      >
        <ReserveCheckTime type="checkout" time={targetSlot.check_out_time} />
        <IconDoorExit size={14} />
      </div>
    );
  }
  if (hasCheckedIn && !canCheckOut) {
    return (
      <div
        className="inline-flex items-center rounded-full \
      border border-disabled bg-white px-3 py-1 text-sm \
      text-gray-400 shadow-sm"
      >
        <ReserveCheckTime type="checkin" time={targetSlot.check_in_time} />
        <IconDoorExit size={14} />
      </div>
    );
  }
  if (hasCheckedIn) {
    return (
      <>
        <Menu withinPortal={false} shadow="md" width={200}>
          <Menu.Target>
            <div
              role="button"
              className={classNames(
                'rounded-full border px-3 py-1 \
                border-primary text-primary'
              )}
            >
              <span>Check Out</span>
              {/* <CheckIcon className={'h-5 w-5'} style={{verticalAlign: 'bottom', display: 'inline'}} /> */}
            </div>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label className="flex items-center">
              Checkout
              <Tooltip size="sm">
                Checking out will end the reservation early, making it available
                for someone else to use.
              </Tooltip>
            </Menu.Label>
            <Menu.Item
              onClick={(ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                if (!loading) {
                  onCheckout();
                }
              }}
              icon={<IconDoorExit size={14} />}
            >
              Check out early
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </>
    );
  }
  return (
    <button
      disabled={disabled}
      className={classNames(
        ' rounded-full border px-3 py-1',
        disabled
          ? 'cursor-not-allowed border-disabled bg-white text-gray-400'
          : 'border-primary text-primary '
      )}
      onClick={(ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        if (!loading && !schedule.is_pending && canCheckIn && !hasCheckedIn) {
          onCheckin();
        }
      }}
    >
      <TacticTooltip reference={<span>Check In</span>}>
        <span className="text-base font-normal text-tertiary">{title}</span>
      </TacticTooltip>
    </button>
  );
}
